import React, { useState, useEffect } from 'react';
import {
    MessageSquare,
    Search,
    User,
    Clock,
    CheckCheck,
    Send,
    MoreVertical,
    Phone,
    RefreshCw,
    CheckSquare,
    Square,
    X,
    Forward
} from 'lucide-react';
import { useAuthStore } from '@dukene/shared';
import { formatDistanceToNow } from 'date-fns';
import { NewContactModal } from './components/NewContactModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface Contact {
    id: string;
    name: string | null;
    phoneNumber: string;
    lastContactedAt: Date;
    customer?: {
        firstName: string;
        lastName: string;
    };
}

interface Message {
    id: string;
    content: string;
    direction: 'INBOUND' | 'OUTBOUND';
    createdAt: Date;
    status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
}

interface Conversation {
    id: string;
    status: 'ACTIVE' | 'RESOLVED' | 'ARCHIVED';
    lastMessageAt: Date;
    contact: Contact;
    messages: Message[];
    _count?: {
        messages: number;
    };
}

const WhatsAppConversations = () => {
    const { user } = useAuthStore();
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'RESOLVED' | 'ARCHIVED'>('ALL');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [isNewContactModalOpen, setIsNewContactModalOpen] = useState(false);

    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
    const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
    const [forwardTargetId, setForwardTargetId] = useState<string>('');
    const [forwarding, setForwarding] = useState(false);

    const toggleMessageSelection = (messageId: string) => {
        const newSelection = new Set(selectedMessageIds);
        if (newSelection.has(messageId)) {
            newSelection.delete(messageId);
        } else {
            newSelection.add(messageId);
        }
        setSelectedMessageIds(newSelection);
    };

    const handleForward = async () => {
        if (!forwardTargetId || selectedMessageIds.size === 0) return;
        setForwarding(true);
        
        const selectedMsgs = messages
            .filter(m => selectedMessageIds.has(m.id))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            
        const targetConversation = conversations.find(c => c.id === forwardTargetId);
        if (!targetConversation) {
            setForwarding(false);
            return;
        }

        try {
            for (const msg of selectedMsgs) {
                await fetch(`${API_BASE_URL}/whatsapp/messages/${user?.storeId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    },
                    body: JSON.stringify({
                        phoneNumber: targetConversation.contact.phoneNumber,
                        message: msg.content,
                        type: 'text',
                    }),
                });
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            setIsForwardModalOpen(false);
            setIsSelectionMode(false);
            setSelectedMessageIds(new Set());
            alert('Messages forwarded successfully');
        } catch (error) {
            console.error('Error forwarding messages:', error);
            alert('Error forwarding messages');
        } finally {
            setForwarding(false);
        }
    };

    useEffect(() => {
        fetchConversations();

        // Auto-refresh conversations every 10 seconds if enabled
        if (autoRefresh) {
            const interval = setInterval(() => {
                fetchConversations();
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [statusFilter, autoRefresh]);

    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(selectedConversation.id);

            // Auto-refresh messages every 5 seconds when a conversation is selected
            if (autoRefresh) {
                const interval = setInterval(() => {
                    fetchMessages(selectedConversation.id);
                }, 5000);

                return () => clearInterval(interval);
            }
        }
    }, [selectedConversation, autoRefresh]);

    const fetchConversations = async () => {
        if (loading) setLoading(true);
        try {
            const statusParam = statusFilter !== 'ALL' ? `?status=${statusFilter}` : '';
            const response = await fetch(`${API_BASE_URL}/whatsapp/conversations/${user?.storeId}${statusParam}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setConversations(data);
                if (data.length > 0 && !selectedConversation) {
                    setSelectedConversation(data[0]);
                }
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async (conversationId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/conversations/${conversationId}/messages`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setMessages(data.reverse()); // Reverse to show oldest first
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        setSending(true);
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/messages/${user?.storeId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify({
                    phoneNumber: selectedConversation.contact.phoneNumber,
                    message: newMessage,
                    type: 'text',
                }),
            });

            if (response.ok) {
                setNewMessage('');
                await fetchMessages(selectedConversation.id);
            } else {
                alert('Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Error sending message');
        } finally {
            setSending(false);
        }
    };

    const resolveConversation = async (conversationId: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/conversations/${conversationId}/resolve`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });

            if (response.ok) {
                await fetchConversations();
                setSelectedConversation(null);
                setIsSelectionMode(false);
                setSelectedMessageIds(new Set());
            }
        } catch (error) {
            console.error('Error resolving conversation:', error);
        }
    };

    const filteredConversations = conversations.filter((conv) =>
        conv.contact.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.customer?.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.contact.customer?.lastName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getContactName = (contact: Contact) => {
        if (contact.customer) {
            return `${contact.customer.firstName} ${contact.customer.lastName}`;
        }
        return contact.name || contact.phoneNumber;
    };
    const formatWhatsAppText = (text: string) => {
        if (!text) return null;
        
        // WhatsApp Markdown parsing for *bold*, _italic_, ~strikethrough~
        const parseLine = (line: string, lineIndex: number) => {
            const parts = line.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g);
            return parts.map((part, i) => {
                const key = `${lineIndex}-${i}`;
                if (part.startsWith('*') && part.endsWith('*')) {
                    return <strong key={key} className="font-bold">{part.slice(1, -1)}</strong>;
                }
                if (part.startsWith('_') && part.endsWith('_')) {
                    return <em key={key} className="italic">{part.slice(1, -1)}</em>;
                }
                if (part.startsWith('~') && part.endsWith('~')) {
                    return <del key={key} className="line-through">{part.slice(1, -1)}</del>;
                }
                return part;
            });
        };

        return text.split('\n').map((line, i, arr) => (
            <React.Fragment key={i}>
                {parseLine(line, i)}
                {i < arr.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col">
            {/* Header */}
            <div className="bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">WhatsApp Conversations</h1>
                        <p className="text-gray-600 text-sm">Manage customer conversations • Auto-refresh {autoRefresh ? 'ON' : 'OFF'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsNewContactModalOpen(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                        >
                            New Contact
                        </button>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}
                        >
                            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                            {autoRefresh ? 'Live' : 'Paused'}
                        </button>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as any)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="ALL">All Status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="RESOLVED">Resolved</option>
                            <option value="ARCHIVED">Archived</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Conversations List */}
                <div className="w-80 bg-white border-r flex flex-col">
                    {/* Search */}
                    <div className="p-4 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search conversations..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    {/* Conversation List */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredConversations.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p>No conversations found</p>
                            </div>
                        ) : (
                            filteredConversations.map((conversation) => (
                                <button
                                    key={conversation.id}
                                    onClick={() => {
                                        setSelectedConversation(conversation);
                                        setIsSelectionMode(false);
                                        setSelectedMessageIds(new Set());
                                    }}
                                    className={`w-full p-4 border-b hover:bg-gray-50 text-left transition-colors ${selectedConversation?.id === conversation.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                            <User className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <p className="font-semibold text-gray-900 truncate">
                                                    {getContactName(conversation.contact)}
                                                </p>
                                                <span className="text-xs text-gray-500">
                                                    {formatDistanceToNow(new Date(conversation.lastMessageAt), { addSuffix: true })}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 truncate">
                                                {conversation.contact.phoneNumber}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${conversation.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                                                    conversation.status === 'RESOLVED' ? 'bg-blue-100 text-blue-700' :
                                                        'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {conversation.status}
                                                </span>
                                                {conversation.messages[0] && (
                                                    <span className="text-xs text-gray-500 truncate">
                                                        {conversation.messages[0].content.substring(0, 30)}...
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                {selectedConversation ? (
                    <div className="flex-1 flex flex-col bg-gray-50">
                        {/* Chat Header */}
                        {isSelectionMode ? (
                            <div className="bg-blue-50 border-b px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <button onClick={() => { setIsSelectionMode(false); setSelectedMessageIds(new Set()); }} className="p-2 hover:bg-blue-100 rounded-lg">
                                        <X className="w-5 h-5 text-blue-900" />
                                    </button>
                                    <span className="font-medium text-blue-900">{selectedMessageIds.size} Selected</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsForwardModalOpen(true)}
                                        disabled={selectedMessageIds.size === 0}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                    >
                                        <Forward className="w-4 h-4" />
                                        Forward
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">
                                            {getContactName(selectedConversation.contact)}
                                        </h3>
                                        <p className="text-sm text-gray-600 flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {selectedConversation.contact.phoneNumber}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIsSelectionMode(true)}
                                        className="px-3 py-1.5 hover:bg-gray-100 rounded-lg text-gray-600 text-sm font-medium flex items-center gap-1.5"
                                        title="Select Messages"
                                    >
                                        <CheckSquare className="w-4 h-4" />
                                        Select
                                    </button>
                                    {selectedConversation.status === 'ACTIVE' && (
                                        <button
                                            onClick={() => resolveConversation(selectedConversation.id)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                        >
                                            Mark as Resolved
                                        </button>
                                    )}
                                    <button className="p-2 hover:bg-gray-100 rounded-lg">
                                        <MoreVertical className="w-5 h-5 text-gray-600" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex items-end gap-2 ${message.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}
                                >
                                    {isSelectionMode && message.direction === 'INBOUND' && (
                                        <button onClick={() => toggleMessageSelection(message.id)} className="text-gray-400 hover:text-blue-600 mb-2">
                                            {selectedMessageIds.has(message.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5" />}
                                        </button>
                                    )}
                                    <div
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            if (!isSelectionMode) {
                                                setIsSelectionMode(true);
                                                toggleMessageSelection(message.id);
                                            }
                                        }}
                                        onClick={() => isSelectionMode && toggleMessageSelection(message.id)}
                                        className={`max-w-md px-4 py-2 rounded-lg ${isSelectionMode ? 'cursor-pointer transition-opacity hover:opacity-90' : ''} ${message.direction === 'OUTBOUND'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-900 border border-gray-200'
                                            }`}
                                    >
                                        <p className="text-sm whitespace-pre-wrap">{formatWhatsAppText(message.content)}</p>
                                        <div className={`flex items-center gap-1 mt-1 text-xs ${message.direction === 'OUTBOUND' ? 'text-blue-100' : 'text-gray-500'
                                            }`}>
                                            <Clock className="w-3 h-3" />
                                            {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                                            {message.direction === 'OUTBOUND' && (
                                                <CheckCheck className={`w-4 h-4 ml-1 ${message.status === 'READ' ? 'text-blue-200' : 'text-blue-300'
                                                    }`} />
                                            )}
                                        </div>
                                    </div>
                                    {isSelectionMode && message.direction === 'OUTBOUND' && (
                                        <button onClick={() => toggleMessageSelection(message.id)} className="text-gray-400 hover:text-blue-600 mb-2">
                                            {selectedMessageIds.has(message.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5" />}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Message Input */}
                        {selectedConversation.status === 'ACTIVE' && (
                            <div className="bg-white border-t px-6 py-4">
                                <div className="flex items-end gap-3">
                                    <textarea
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                sendMessage();
                                            }
                                        }}
                                        placeholder="Type a message..."
                                        rows={3}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                                    />
                                    <button
                                        onClick={sendMessage}
                                        disabled={sending || !newMessage.trim()}
                                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 h-fit"
                                    >
                                        {sending ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5" />
                                                Send
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Press Enter to send, Shift+Enter for new line</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-500">
                        <MessageSquare className="w-16 h-16 mb-4 text-gray-300" />
                        <h3 className="text-xl font-medium text-gray-900 mb-2">No Conversation Selected</h3>
                        <p>Select a conversation from the left to start messaging</p>
                    </div>
                )}
            </div>

            <NewContactModal
                isOpen={isNewContactModalOpen}
                onClose={() => setIsNewContactModalOpen(false)}
                onSuccess={() => {
                    fetchConversations();
                }}
            />

            {/* Forward Modal */}
            {isForwardModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">Forward to...</h3>
                            <button onClick={() => setIsForwardModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 max-h-96 overflow-y-auto">
                            {conversations
                                .filter(c => c.status === 'ACTIVE' && c.id !== selectedConversation?.id)
                                .map(conv => (
                                    <button
                                        key={conv.id}
                                        onClick={() => setForwardTargetId(conv.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${forwardTargetId === conv.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}`}
                                    >
                                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                            <User className="w-5 h-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{getContactName(conv.contact)}</p>
                                            <p className="text-sm text-gray-500">{conv.contact.phoneNumber}</p>
                                        </div>
                                    </button>
                                ))
                            }
                            {conversations.filter(c => c.status === 'ACTIVE' && c.id !== selectedConversation?.id).length === 0 && (
                                <p className="text-center text-gray-500 py-4">No active conversations with open windows to forward to.</p>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => setIsForwardModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleForward}
                                disabled={!forwardTargetId || forwarding}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {forwarding ? 'Forwarding...' : 'Forward'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppConversations;
